import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getActiveEvent, getProgram } from '../lib/cms';

export default function Eventet() {
  const [eventData, setEventData] = useState(null);
  const [program, setProgram] = useState([]);

  useEffect(() => {
    getActiveEvent().then(setEventData);
    getProgram().then(setProgram);
  }, []);

  const billetlink = eventData?.billetlink || "https://billet.eventbilletten.dk/seller/foreningen-rbike-zd1k";

  return (
    <div className="bg-[#090a0f] text-slate-100 min-h-screen pb-20">
      <Helmet>
        <title>Eventet - Foreningen ØrBike</title>
        <meta
          name="description"
          content="ØrBike afholdes altid den anden lørdag i november i Ørbæk Midtpunkt. 10 timers energi, sved, musik og motivation til fordel for børn med kritisk sygdom."
        />
        <meta property="og:title" content="Eventet - Foreningen ØrBike" />
        <meta property="og:description" content="Oplev Danmarks vildeste indoor bike fest med +230 cykler i Ørbæk Midtpunkt." />
      </Helmet>

      {/* Page Header */}
      <section className="relative pt-36 sm:pt-40 pb-16 sm:pb-20 bg-gradient-to-b from-[#121422] to-[#090a0f] border-b border-pink-500/20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-400 border border-pink-500/30 mb-4">
            10 timers indoor bike fest
          </span>
          <h1 className="text-4xl sm:text-6xl font-black font-display text-white uppercase tracking-tight">
            ØrBike Eventet
          </h1>
          <p className="text-pink-300 font-display text-lg sm:text-2xl font-bold uppercase mt-2">
            Vi skaber en indoor bike fest til fordel for børn med kritisk sygdom
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href={billetlink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm uppercase tracking-wider shadow-[0_0_25px_rgba(255,42,141,0.5)] hover:scale-105 transition-transform"
            >
              <i className="fa-solid fa-ticket" aria-hidden="true"></i>
              <span>Køb billet på Eventbilletten</span>
            </a>
          </div>
        </div>
      </section>

      {/* Introduktion & Fakta */}
      <section className="py-16 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 space-y-4">
            <h2 className="text-2xl sm:text-4xl font-black text-white font-display uppercase tracking-tight">
              Sammen skaber vi smil, ét tråd ad gangen
            </h2>
            <p className="text-slate-300 text-base leading-relaxed">
              ØrBike afholdes <strong>altid den anden lørdag i november</strong> – en dag fyldt med energi, musik og fællesskab.
            </p>
            <p className="text-slate-300 text-base leading-relaxed">
              Siden det første event i <strong>2014</strong> har ØrBike hvert år samlet hundredevis af ryttere i <strong>Ørbæk Midtpunkt</strong> til en dag, hvor vi cykler for livet og for hinanden.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed">
              I <strong>2024</strong> fejrede vi vores <strong>10-års jubilæum</strong> med en uforglemmelig indoor bike fest – og succesen fortsætter!
            </p>
          </div>

          <div className="md:col-span-5">
            <img
              src="https://assets.auxodev.dk/assets/c6ea7980-b639-49a1-80cc-f044c1665109/dreamfour-9903.jpg"
              alt="ØrBike Deltagere"
              className="rounded-2xl border border-pink-500/30 object-cover w-full shadow-[0_0_30px_rgba(255,42,141,0.2)]"
            />
          </div>
        </div>

        {/* Dato og status for 2025 & 2026 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800">
            <span className="text-xs uppercase font-mono font-bold text-slate-400">Afholdt event</span>
            <h3 className="text-2xl font-bold font-display text-white uppercase mt-1">
              ØrBike 2025 – 8. november 2025 (Udsolgt)
            </h3>
            <p className="text-sm text-slate-300 mt-2">
              Eventet i 2025 blev en kæmpe succes med udsolgte cykler, fantastisk fællesskab og sved på panden.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-gradient-to-b from-pink-950/40 to-slate-900/80 border border-pink-500/40 shadow-[0_0_25px_rgba(255,42,141,0.2)]">
            <span className="text-xs uppercase font-mono font-bold text-pink-400">Næste store event</span>
            <h3 className="text-2xl font-bold font-display text-white uppercase mt-1">
              ØrBike 2026 – 14. november 2026
            </h3>
            <p className="text-sm text-slate-200 mt-2">
              Glæd dig til endnu en dag med <strong>10 timers energi, sved, musik og motivation</strong> i hjertet af Ørbæk.
            </p>
          </div>
        </div>

        {/* Dagens program / Tidsplan */}
        <div>
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="text-xs uppercase tracking-widest font-bold text-pink-400 font-mono">
              Tidsplan
            </span>
            <h3 className="text-3xl sm:text-4xl font-black text-white font-display uppercase tracking-tight mt-1">
              Dagens program
            </h3>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">
              Tidsplanen gælder for den store cykeldag i Ørbæk Midtpunkt
            </p>
          </div>

          <div className="space-y-3">
            {program.map((item, index) => (
              <div
                key={item.id || index}
                className="p-5 rounded-2xl bg-[#12141f] border border-slate-800 hover:border-pink-500/40 transition-colors flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
              >
                <div className="flex items-center gap-4">
                  <span className="px-3.5 py-1.5 rounded-xl bg-pink-500/20 text-pink-400 font-display font-bold text-lg min-w-[5.5rem] text-center border border-pink-500/30">
                    {item.tidspunkt}
                  </span>
                  <div>
                    <h4 className="font-bold text-white text-base sm:text-lg font-display uppercase">
                      {item.titel}
                    </h4>
                    <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                      {item.beskrivelse}
                    </p>
                  </div>
                </div>
                {item.instruktor_artist && (
                  <div className="text-xs font-semibold text-purple-300 flex items-center gap-1.5 shrink-0 bg-purple-500/10 px-3 py-1 rounded-full border border-purple-500/20">
                    <i className="fa-solid fa-microphone text-pink-400" aria-hidden="true"></i>
                    <span>{item.instruktor_artist}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Instruktørerne */}
        <div className="bg-[#12141e] border border-pink-500/30 rounded-3xl p-8 sm:p-10 space-y-6">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-purple-500/20 text-purple-400 border border-purple-500/30">
            Instruktørerne
          </span>
          <h3 className="text-3xl sm:text-4xl font-black text-white font-display uppercase tracking-tight">
            På ØrBike skaber vi energi, fællesskab – og en fest på cyklen!
          </h3>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            Vi gør os umage for at samle nogle af <strong>Danmarks dygtigste og mest engagerede instruktører</strong>, som kan skabe en helt særlig stemning i salen. <strong>10 timer på cyklen</strong> kan virke som en udfordring – men med den rette musik, energi og motivation flyver tiden afsted.
          </p>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            Hos ØrBike blander vi <strong>erfarne profiler</strong> med <strong>nyere talenter</strong>, så du får den perfekte kombination af erfaring, nytænkning og passion.
          </p>
          <div className="p-4 rounded-xl bg-pink-500/10 border border-pink-500/30 text-pink-300 text-sm font-semibold flex items-center gap-3">
            <i className="fa-solid fa-music text-pink-400 text-lg" aria-hidden="true"></i>
            <span>Glæd dig til musik, energi og masser af smil – ét tråd ad gangen!</span>
          </div>
        </div>

        {/* Pedaler, Klamper og Sko */}
        <div className="space-y-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest font-bold text-pink-400 font-mono">
              Udstyr & Klamper
            </span>
            <h3 className="text-3xl sm:text-4xl font-black text-white font-display uppercase tracking-tight mt-1">
              Pedaler og sko
            </h3>
            <p className="text-slate-300 text-sm mt-1">
              Hos ØrBike cykler vi på cykler udstyret med <strong>Look Classic</strong> og <strong>Shimano SPD</strong> pedaler.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800">
              <div className="w-10 h-10 rounded-full bg-pink-500/20 text-pink-400 flex items-center justify-center mb-3">
                <i className="fa-solid fa-shoe-prints" aria-hidden="true"></i>
              </div>
              <h4 className="font-bold text-white text-lg font-display uppercase">Look Delta Klamper</h4>
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                De klassiske røde klamper, som ofte bruges i spinninglokaler. Sikrer solidt og effektivt tråd på cyklen.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center mb-3">
                <i className="fa-solid fa-bicycle" aria-hidden="true"></i>
              </div>
              <h4 className="font-bold text-white text-lg font-display uppercase">Shimano SPD Klamper</h4>
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                De små metal-klamper, som bruges til mountainbike og spinning. Nemme at klikke i og ud.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-3">
                <i className="fa-solid fa-person-walking" aria-hidden="true"></i>
              </div>
              <h4 className="font-bold text-white text-lg font-display uppercase">Kondisko til hallerne</h4>
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                Vi har ikke beslag til almindelige sko på cyklerne, men medbring gerne et par kondisko til at gå rundt i hallerne.
              </p>
            </div>
          </div>
        </div>

        {/* Billet CTA Sektion */}
        <div className="text-center bg-gradient-to-r from-pink-950/40 to-purple-950/40 border border-pink-500/30 rounded-3xl p-8 sm:p-12 shadow-xl">
          <h3 className="text-2xl sm:text-4xl font-black text-white font-display uppercase">
            Vil du være med til årets cykelfest?
          </h3>
          <p className="text-slate-300 text-sm sm:text-base mt-2 max-w-xl mx-auto">
            Sikre din cykel nu på Eventbilletten. Vælg mellem Priority og Essential cykler for 2026.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <a
              href={billetlink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm uppercase tracking-wider shadow-lg hover:scale-105 transition-transform"
            >
              <i className="fa-solid fa-ticket" aria-hidden="true"></i>
              <span>Køb billet på Eventbilletten</span>
            </a>
            <Link
              to="/billetter"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-slate-900 hover:bg-slate-800 border border-slate-700 text-white font-bold text-sm uppercase tracking-wider transition-colors"
            >
              <span>Se priser og forplejning</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
